import { useState, useMemo } from 'react';
import { box, input, label, btnPlain, Question, Pick, PickMany } from './surveyFormParts';
import {
  DESK_NETWORKS, EXTRA_LABELS, RAG_LABELS, YES_NO_LABELS, kitItemsOf, deskView, deskTotals, listOf, mapOf,
} from '../../utils/siteSurveys';

// The desk survey's own sections (migration 0409), inside SurveyForm: the
// standards, one card per desk, the totals Ops works out from them, and the
// adjustments, additional equipment and risks found on the walk. The parent
// holds the state and saves it; these sections only edit it.

const NETWORK_OPTIONS = Object.fromEntries(DESK_NETWORKS.map(n => [n, n]));
const newKey = () => (globalThis.crypto?.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);
// Two comma lists holding the same networks, in any order.
const sameSet = (a, b) => listOf(a).sort().join() === listOf(b).sort().join();
const n = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

/** "66-81, 90, 1A" as desk numbers, a range at most 200 long. */
export function desksFrom(text) {
  const out = [];
  for (const part of String(text || '').split(/[,\s]+/).filter(Boolean)) {
    const m = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (m && Number(m[2]) >= Number(m[1]) && Number(m[2]) - Number(m[1]) < 200) {
      for (let i = Number(m[1]); i <= Number(m[2]); i++) out.push(String(i));
    } else out.push(part);
  }
  return out;
}

function Standards({ survey, parts, setParts, setField, locked }) {
  const items = kitItemsOf(survey);
  const [newItem, setNewItem] = useState('');
  const [msg, setMsg] = useState('');
  const standards = parts.standards;
  const setStd = (i, patch) => setParts({ ...parts, standards: standards.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setKit = (i, item, v) => setStd(i, { kit_json: { ...mapOf(standards[i].kit_json), [item]: v === '' ? undefined : n(v) } });
  const add = () => setParts({ ...parts, standards: [...standards, { key: newKey(), name: `Standard ${standards.length + 1}`, networks: '', sockets: null, kit_json: {} }] });
  const remove = (i) => {
    const used = parts.desks.filter(d => d.standard_key === standards[i].key).map(d => d.desk);
    if (used.length) { setMsg(`${standards[i].name} is the standard for desk ${used.slice(0, 5).join(', ')}${used.length > 5 ? ` and ${used.length - 5} more` : ''}. Move ${used.length === 1 ? 'it' : 'them'} to another standard first.`); return; }
    setMsg('');
    setParts({ ...parts, standards: standards.filter((_, j) => j !== i) });
  };
  const addItem = () => {
    const name = newItem.trim();
    if (!name || name.includes(',')) { setMsg('Name the kit item, without a comma.'); return; }
    if (items.some(i => i.toLowerCase() === name.toLowerCase())) { setMsg(`${name} is already counted.`); return; }
    setMsg(''); setNewItem('');
    setField('desk_kit_items', [...items, name].join(','));
  };
  const removeItem = (item) => setField('desk_kit_items', items.filter(i => i !== item).join(','));

  return (
    <Question title="Standards" hint="What each kind of desk gets: its networks, the minimum sockets it needs and the kit per desk. A desk can differ from its standard on its own card." tour="survey-desk-standards">
      <div className="overflow-x-auto">
        <table className="text-[10px] border-collapse">
          <thead>
            <tr>
              <th className="text-left px-1.5 py-1 text-[9px] uppercase text-gray-400">Item</th>
              {standards.map((s, i) => (
                <th key={s.key} className="px-1 py-1 min-w-[130px]">
                  <div className="flex items-center gap-1">
                    <input className={`${box} w-full font-bold`} value={s.name} disabled={locked} aria-label="Standard name" onChange={e => setStd(i, { name: e.target.value })} />
                    {!locked && <button type="button" onClick={() => remove(i)} className="text-gray-400 hover:text-red-600 text-[14px]" aria-label={`Remove ${s.name}`}>×</button>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-1.5 py-1 font-semibold text-navy">Networks</td>
              {standards.map((s, i) => (
                <td key={s.key} className="px-1 py-1"><PickMany options={NETWORK_OPTIONS} value={s.networks} disabled={locked} onChange={v => setStd(i, { networks: v || '' })} /></td>
              ))}
            </tr>
            <tr>
              <td className="px-1.5 py-1 font-semibold text-navy">Minimum sockets</td>
              {standards.map((s, i) => (
                <td key={s.key} className="px-1 py-1"><input className={`${box} w-16`} inputMode="numeric" value={s.sockets ?? ''} disabled={locked} aria-label={`${s.name} minimum sockets`} onChange={e => setStd(i, { sockets: e.target.value === '' ? null : e.target.value })} /></td>
              ))}
            </tr>
            {items.map(item => (
              <tr key={item}>
                <td className="px-1.5 py-1 text-gray-700 whitespace-nowrap">
                  {item}
                  {!locked && <button type="button" onClick={() => removeItem(item)} className="ml-1 text-gray-300 hover:text-red-600" aria-label={`Stop counting ${item}`}>×</button>}
                </td>
                {standards.map((s, i) => (
                  <td key={s.key} className="px-1 py-1"><input className={`${box} w-16`} inputMode="numeric" value={mapOf(s.kit_json)[item] ?? ''} disabled={locked} aria-label={`${s.name} ${item}`} onChange={e => setKit(i, item, e.target.value)} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!locked && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button type="button" onClick={add} className="text-[10px] font-semibold text-gold hover:underline">+ Add a standard</button>
          <span className="text-gray-300">|</span>
          <input className={`${box} w-40`} value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Another kit item" aria-label="Another kit item" />
          <button type="button" onClick={addItem} className="text-[10px] font-semibold text-gold hover:underline">+ Count it</button>
        </div>
      )}
      {msg && <div role="status" className="text-[10px] text-red-600 mt-1.5">{msg}</div>}
    </Question>
  );
}

function YesNo({ value, onChange, disabled }) {
  return <Pick options={YES_NO_LABELS} value={value} onChange={onChange} disabled={disabled} />;
}

function DeskCard({ desk, i, survey, parts, update, remove, copyAbove, locked, open, toggle }) {
  const items = kitItemsOf(survey);
  const v = deskView(desk, parts.standards);
  const set = (patch) => update(i, patch);
  const setMap = (k, item, val) => set({ [k]: { ...mapOf(desk[k]), [item]: val === '' ? undefined : val } });
  const badges = [];
  if (!desk.checked) badges.push(['Not answered', 'bg-gray-100 text-gray-600']);
  if (desk.checked === 'no') badges.push(['Not checked', 'bg-amber-50 text-amber-700']);
  if (v.shortfall !== null && v.shortfall < 0) badges.push([`${-v.shortfall} ${v.shortfall === -1 ? 'socket' : 'sockets'} short`, 'bg-red-50 text-red-700']);
  if (v.missing.length) badges.push([`No ${v.missing.join(', ')} port`, 'bg-red-50 text-red-700']);
  if (desk.access_issue === 'yes') badges.push(['Access issue', 'bg-amber-50 text-amber-700']);

  return (
    <div className="border border-gray-200 rounded mb-1.5 bg-white">
      <button type="button" onClick={toggle} aria-expanded={open} className="w-full flex flex-wrap items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50">
        <span className="font-mono font-bold text-navy text-[11px] w-12 shrink-0">{desk.desk || '?'}</span>
        <span className="text-[10px] text-gray-600 min-w-0 truncate">{v.std?.name || 'No standard'}{desk.user_name ? ` · ${desk.user_name}` : ''}</span>
        <span className="flex flex-wrap gap-1 ml-auto">
          {badges.map(([t, cls]) => <span key={t} className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${cls}`}>{t}</span>)}
          <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 pt-1 border-t border-gray-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className={label}>Desk</label><input className={input} value={desk.desk || ''} disabled={locked} onChange={e => set({ desk: e.target.value })} /></div>
            <div><label className={label}>User</label><input className={input} value={desk.user_name || ''} disabled={locked} onChange={e => set({ user_name: e.target.value })} /></div>
            <div className="col-span-2">
              <label className={label}>Standard</label>
              <select className={input} value={desk.standard_key || ''} disabled={locked} onChange={e => set({ standard_key: e.target.value || null })}>
                <option value="">No standard</option>
                {parts.standards.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-span-2"><div className={label}>Desk checked?</div><YesNo value={desk.checked} disabled={locked} onChange={x => set({ checked: x })} /></div>
            <div className="col-span-2"><div className={label}>Sit/stand desk?</div><YesNo value={desk.sit_stand} disabled={locked} onChange={x => set({ sit_stand: x })} /></div>
            <div className="col-span-2 sm:col-span-4">
              <div className={label}>Networks needed {listOf(desk.networks).length ? '(this desk only)' : v.std ? `(${v.std.name}: ${v.networks.join(', ') || 'none'})` : ''}</div>
              <PickMany options={NETWORK_OPTIONS} value={desk.networks || v.networks.join(',')} disabled={locked} onChange={x => set({ networks: sameSet(x, v.std?.networks) ? null : x })} />
            </div>
            <div><label className={label}>Sockets needed</label><input className={input} inputMode="numeric" value={desk.sockets_required ?? ''} placeholder={v.std?.sockets ?? ''} disabled={locked} onChange={e => set({ sockets_required: e.target.value })} /></div>
            <div><label className={label}>Sockets available</label><input className={input} inputMode="numeric" value={desk.sockets_available ?? ''} disabled={locked} onChange={e => set({ sockets_available: e.target.value })} /></div>
            <div><label className={label}>Extra sockets needed</label><input className={input} inputMode="numeric" value={desk.extra_sockets ?? ''} disabled={locked} onChange={e => set({ extra_sockets: e.target.value })} /></div>
            <div><label className={label}>Floor box / power ref</label><input className={input} value={desk.floor_box || ''} disabled={locked} onChange={e => set({ floor_box: e.target.value })} /></div>
            {DESK_NETWORKS.map(net => (
              <div key={net}>
                <label className={label}>{net} port{v.networks.includes(net) ? ' (needed)' : ''}</label>
                <input className={input} value={mapOf(desk.ports_json)[net] || ''} placeholder="As labelled, or No" disabled={locked} onChange={e => setMap('ports_json', net, e.target.value)} />
              </div>
            ))}
            <div className="col-span-2 sm:col-span-4"><div className={label}>Desktop machines at the desk</div><PickMany options={NETWORK_OPTIONS} value={desk.desktops} disabled={locked} onChange={x => set({ desktops: x })} /></div>
            <div className="col-span-2"><div className={label}>Access issue?</div><YesNo value={desk.access_issue} disabled={locked} onChange={x => set({ access_issue: x })} /></div>
            {desk.access_issue === 'yes' && <div className="col-span-2"><label className={label}>What is the issue?</label><input className={input} value={desk.access_detail || ''} disabled={locked} onChange={e => set({ access_detail: e.target.value })} /></div>}
          </div>
          <div className="mt-2 overflow-x-auto">
            <table className="text-[10px]">
              <thead><tr className="text-[8px] uppercase text-gray-400"><th className="text-left pr-2">Kit</th><th className="text-left pr-2">Needed here</th><th className="text-left">Already here and reusable</th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item}>
                    <td className="pr-2 py-0.5 text-gray-700 whitespace-nowrap">{item}</td>
                    <td className="pr-2 py-0.5"><input className={`${box} w-16`} inputMode="numeric" value={mapOf(desk.kit_json)[item] ?? ''} placeholder={String(mapOf(v.std?.kit_json)[item] ?? '')} aria-label={`${item} needed at desk ${desk.desk}`} disabled={locked} onChange={e => setMap('kit_json', item, e.target.value)} /></td>
                    <td className="py-0.5"><input className={`${box} w-16`} inputMode="numeric" value={mapOf(desk.reusable_json)[item] ?? ''} aria-label={`${item} already at desk ${desk.desk}`} disabled={locked} onChange={e => setMap('reusable_json', item, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[9px] text-gray-400 mt-0.5">A blank "needed here" takes the standard's figure, shown faded.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div><label className={label}>Other kit (heaters, chargers)</label><input className={input} value={desk.other_kit || ''} disabled={locked} onChange={e => set({ other_kit: e.target.value })} /></div>
            <div><label className={label}>Notes or potential issues</label><input className={input} value={desk.notes || ''} disabled={locked} onChange={e => set({ notes: e.target.value })} /></div>
          </div>
          {!locked && (
            <div className="flex gap-3 mt-2">
              {i > 0 && <button type="button" onClick={() => copyAbove(i)} className="text-[10px] font-semibold text-gold hover:underline">Same as the desk above</button>}
              <button type="button" onClick={() => remove(i)} className="text-[10px] font-semibold text-red-600 hover:underline ml-auto">Remove desk {desk.desk}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Desks({ survey, parts, setParts, locked }) {
  const [adding, setAdding] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [filter, setFilter] = useState('all');
  const [msg, setMsg] = useState('');
  const desks = parts.desks;
  const update = (i, patch) => setParts({ ...parts, desks: desks.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const remove = (i) => setParts({ ...parts, desks: desks.filter((_, j) => j !== i) });
  // The answers a run of similar desks share; the desk's own number, user and ports stay its own.
  const copyAbove = (i) => {
    const a = desks[i - 1];
    update(i, { standard_key: a.standard_key, networks: a.networks, sockets_required: a.sockets_required, sockets_available: a.sockets_available,
      sit_stand: a.sit_stand, access_issue: a.access_issue, access_detail: a.access_detail, kit_json: a.kit_json, desktops: a.desktops });
  };
  const addDesks = () => {
    const wanted = desksFrom(adding);
    const have = new Set(desks.map(d => String(d.desk).toLowerCase()));
    const fresh = wanted.filter(d => !have.has(d.toLowerCase()));
    if (!wanted.length) { setMsg('Type desk numbers, such as 66-81 or 12, 14, 1A.'); return; }
    const std = parts.standards.length === 1 ? parts.standards[0].key : null;
    setParts({ ...parts, desks: [...desks, ...fresh.map(d => ({ _key: newKey(), desk: d, standard_key: std }))] });
    setMsg(fresh.length < wanted.length ? `${wanted.length - fresh.length} already listed, so ${fresh.length} added.` : `${fresh.length} ${fresh.length === 1 ? 'desk' : 'desks'} added.`);
    setAdding('');
  };
  const shown = desks.map((d, i) => [d, i]).filter(([d]) => {
    if (filter === 'all') return true;
    const v = deskView(d, parts.standards);
    if (filter === 'todo') return !d.checked;
    return (v.shortfall !== null && v.shortfall < 0) || v.missing.length || d.access_issue === 'yes';
  });

  return (
    <Question title={`Desks (${desks.length})`} hint="One card per desk. Open a card to fill it in. Similar desks can copy the desk above." tour="survey-desks">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {[['all', 'All'], ['todo', 'Not answered'], ['issues', 'With issues']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2.5 py-1 text-[9px] font-semibold rounded-full border ${filter === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>{l}</button>
        ))}
      </div>
      {shown.map(([d, i]) => (
        <DeskCard key={d.id || d._key || `${d.desk}-${i}`} desk={d} i={i} survey={survey} parts={parts} update={update} remove={remove} copyAbove={copyAbove}
          locked={locked} open={openKey === i} toggle={() => setOpenKey(k => (k === i ? null : i))} />
      ))}
      {desks.length > 0 && shown.length === 0 && <div className="text-[10px] text-gray-400">No desks match.</div>}
      {!locked && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <input className={`${box} w-48`} value={adding} onChange={e => setAdding(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addDesks(); }} placeholder="66-81, 90" aria-label="Desk numbers to add" />
          <button type="button" onClick={addDesks} className={btnPlain}>Add desks</button>
          {msg && <span role="status" className="text-[10px] text-gray-600">{msg}</span>}
        </div>
      )}
    </Question>
  );
}

function Totals({ survey, parts }) {
  const t = useMemo(() => deskTotals(survey, parts.standards, parts.desks), [survey, parts]);
  if (!parts.desks.length) return null;
  return (
    <Question title="Totals" hint="Worked out from the desks: the standard's kit unless a desk says otherwise, less what is already at the desks." tour="survey-desk-totals">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <table className="text-[10px] w-full">
          <thead><tr className="text-[8px] uppercase text-gray-400"><th className="text-left">Kit</th><th className="text-right">Needed</th><th className="text-right">At desks</th><th className="text-right">To order</th></tr></thead>
          <tbody>
            {Object.entries(t.kit).map(([item, k]) => (
              <tr key={item} className="border-t border-gray-100"><td className="py-0.5">{item}</td><td className="text-right">{k.required}</td><td className="text-right">{k.reusable}</td><td className="text-right font-bold text-navy">{k.order}</td></tr>
            ))}
          </tbody>
        </table>
        <div>
          <table className="text-[10px] w-full mb-2">
            <thead><tr className="text-[8px] uppercase text-gray-400"><th className="text-left">Network</th><th className="text-right">Desks needing it</th><th className="text-right">Ports recorded</th></tr></thead>
            <tbody>
              {Object.entries(t.networks).filter(([, x]) => x.desks).map(([net, x]) => (
                <tr key={net} className="border-t border-gray-100"><td className="py-0.5">{net}</td><td className="text-right">{x.desks}</td><td className={`text-right font-bold ${x.ports < x.desks ? 'text-red-600' : 'text-navy'}`}>{x.ports}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-gray-600">
            {t.desks} desks, {t.unchecked} not answered, {t.socketsShort} short of sockets{t.extraSockets ? `, ${t.extraSockets} extra sockets asked for` : ''}.
          </div>
        </div>
      </div>
      {t.problems.length > 0 && (
        <ul className="list-disc pl-4 mt-2 text-[10px] text-red-700">{t.problems.slice(0, 30).map(p => <li key={p}>{p}</li>)}{t.problems.length > 30 && <li>and {t.problems.length - 30} more</li>}</ul>
      )}
    </Question>
  );
}

// The columns each kind of extra row takes: [field, label, kind of input].
const EXTRA_COLS = {
  adjustment: [['desks', 'Desk', 'text'], ['user_name', 'User', 'text'], ['item', 'Item or adjustment', 'text'], ['must_stay', 'Must stay?', 'yn'],
    ['connection', 'Connection (USB, wireless, Bluetooth)', 'text'], ['compatible', 'Works with the new dock or KVM?', 'yn'], ['finding', 'Potential issue', 'text'], ['action', 'Action', 'text']],
  additional: [['desks', 'Desk(s)', 'text'], ['item', 'Item', 'text'], ['qty', 'Qty', 'num'], ['reason', 'Reason', 'text'], ['client_approved', 'Client approved?', 'yn'], ['notes', 'Notes', 'text']],
  risk: [['item', 'Risk', 'text'], ['reason', 'Why it matters', 'text'], ['check_text', 'What to check', 'text'], ['rag', 'RAG', 'rag'], ['finding', 'Finding', 'text'], ['action', 'Action or owner', 'text']],
};
const EXTRA_HINTS = {
  adjustment: 'Ergonomic mice, split keyboards, monitor arms, screen readers and anything set up through a DSE assessment. It stays and must work with the new kit.',
  additional: 'Power modules, patch leads, cable management, monitor arms. Each line needs client approval before it is ordered.',
  risk: 'Check each on the walk and record what you found.',
};

// A table of extra rows of one kind. `cols` overrides the columns: each is
// [field, label, type], where type is 'text', 'num', 'date', 'yn', 'rag' or a
// { value: label } map to choose from. The desk move uses its own columns.
export function Extras({ kind, parts, setParts, locked, cols, title, hint, tour }) {
  const rows = parts.extras.filter(e => e.kind === kind);
  const set = (i, patch) => {
    let k = -1;
    setParts({ ...parts, extras: parts.extras.map(e => (e.kind === kind && ++k === i ? { ...e, ...patch } : e)) });
  };
  const remove = (i) => {
    let k = -1;
    setParts({ ...parts, extras: parts.extras.filter(e => !(e.kind === kind && ++k === i)) });
  };
  const add = () => setParts({ ...parts, extras: [...parts.extras, { kind, item: '' }] });
  return (
    <Question title={`${title || EXTRA_LABELS[kind]} (${rows.filter(r => r.item).length})`} hint={hint ?? EXTRA_HINTS[kind]} tour={tour || `survey-desk-${kind}`}>
      {rows.map((r, i) => (
        <div key={r.id || i} className="border border-gray-100 rounded p-2 mb-1.5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            {(cols || EXTRA_COLS[kind]).map(([f, l, type]) => (
              <div key={f} className={f === 'item' ? 'sm:col-span-2' : ''}>
                <div className={label}>{l}</div>
                {type === 'yn' ? <Pick options={YES_NO_LABELS} value={r[f]} disabled={locked} onChange={x => set(i, { [f]: x })} />
                  : type === 'rag' ? <Pick options={RAG_LABELS} value={r[f]} disabled={locked} onChange={x => set(i, { [f]: x })} />
                  : typeof type === 'object' ? <Pick options={type} value={r[f]} disabled={locked} onChange={x => set(i, { [f]: x })} />
                  : type === 'date' ? <input type="date" className={input} value={r[f] ?? ''} disabled={locked} aria-label={l} onChange={e => set(i, { [f]: e.target.value || null })} />
                    : <input className={input} inputMode={type === 'num' ? 'numeric' : undefined} value={r[f] ?? ''} disabled={locked} aria-label={l} onChange={e => set(i, { [f]: e.target.value })} />}
              </div>
            ))}
          </div>
          {!locked && <button type="button" onClick={() => remove(i)} className="text-[9px] text-red-600 hover:underline mt-1">Remove</button>}
        </div>
      ))}
      {!locked && <button type="button" onClick={add} className="text-[10px] font-semibold text-gold hover:underline">+ Add a row</button>}
    </Question>
  );
}

export default function DeskSurveySections({ survey, parts, setParts, setField, locked }) {
  return (
    <>
      <Standards survey={survey} parts={parts} setParts={setParts} setField={setField} locked={locked} />
      <Desks survey={survey} parts={parts} setParts={setParts} locked={locked} />
      <Totals survey={survey} parts={parts} />
      <Extras kind="adjustment" parts={parts} setParts={setParts} locked={locked} />
      <Extras kind="additional" parts={parts} setParts={setParts} locked={locked} />
      <Extras kind="risk" parts={parts} setParts={setParts} locked={locked} />
    </>
  );
}
